import {
  Button,
  ButtonGroup,
  HStack,
  RadioGroup,
  Separator,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../../../shared/stores/RootStore";
import { useEffect, useState } from "react";
import { LngLat } from "mapbox-gl";
import { MapillaryApi } from "../../../../../map/services/MapillaryApi";
import { MapillarySequence } from "../../../../../map/models/Mapillary";
import { toast, toaster } from "../../../../../shared/views/CustomToaster";
import { formatDateString } from "../../../../../shared/utils/format-utils";
import { JobStateApi } from "../../../../../shared/services/JobStateApi";
import { JobStatus } from "../../../../../shared/models/JobState";
import { delay } from "@keita/lanepatrol-sdk";

const MapillaryImportControlsBase = ({
  onCancel,
}: {
  onCancel: () => void;
}) => {
  const { mapStore, osaNodeStore, osaWayStore } = useStores();

  const osaWay = osaWayStore.getSelected()!;

  const [awaitingPoint, setAwaitingPoint] = useState(true);
  const [loading, setLoading] = useState(false);
  const [sequences, setSequences] = useState<MapillarySequence[]>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState<string>();

  const onSelectPoint = async (lngLat: LngLat) => {
    setLoading(true);

    var radius = 0.0002;

    const toastId = toast("loading", `Starting search...`);

    const attempt = async (): Promise<MapillarySequence[]> => {
      console.log("Radius:", radius);
      return await MapillaryApi.querySequences({
        lat: lngLat.lat,
        lng: lngLat.lng,
        radius: radius,
      })
        .then(({ data }) => data)
        .catch((e) => {
          console.error(e);
          return [];
        });
    };

    var results: MapillarySequence[] = [];

    while (radius < 0.01) {
      results = await attempt();

      if (results.length) {
        break;
      }

      toaster.update(toastId, {
        title: `No results found. Retrying with bigger search radius: ${radius}`,
      });

      // just so we don't spam the mapillary server
      await delay(1000);

      radius *= 2;
    }

    toaster.remove(toastId);
    if (results.length) {
      setSequences(results);
    } else {
      toast("error", "Could not find any photos near that location");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (awaitingPoint) {
      const stopListening = mapStore.listenForClick((e) => {
        onSelectPoint(e.lngLat);
        setAwaitingPoint(false);
      });

      return stopListening;
    }
    // eslint-disable-next-line
  }, [awaitingPoint]);

  const onConfirm = async () => {
    const toastId = toast("loading", `Initiating import...`);
    setLoading(true);

    return await MapillaryApi.importSequence({
      osaWayId: osaWay.id,
      sequenceId: selectedSequenceId!,
    })
      .then(({ data: jobStateId }) =>
        JobStateApi.poll(jobStateId, 2000, (jobState) => {
          if (
            jobState.status === JobStatus.RUNNING &&
            jobState.progressTotal > 0
          ) {
            toaster.update(toastId, {
              title: `Importing photos (${jobState.progressValue} of ${jobState.progressTotal})`,
            });
          }

          if (jobState.status === JobStatus.FINISHED) {
            toast("success", "Import done");
            onCancel();
            osaWayStore
              .refreshOsaWay(osaWay.id)
              .then(() => osaNodeStore.fetchNodes(osaWay.id));
          }
        })
      )
      .catch(() => {
        toast("error", "An error ocurred. Please retry.");
      })
      .finally(() => {
        toaster.remove(toastId);
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <HStack justifyContent={"center"} p={4}>
        <Spinner size={"lg"} color="primary.500" />
      </HStack>
    );
  }

  if (sequences.length) {
    return (
      <Stack>
        <Text fontSize="sm">Select a set of photos to import</Text>

        <RadioGroup.Root
          colorPalette={"primary"}
          value={selectedSequenceId}
          onValueChange={(e) => setSelectedSequenceId(e.value ?? undefined)}
        >
          <Stack separator={<Separator />} gap={0}>
            {sequences.map((s) => {
              return (
                <RadioGroup.Item
                  key={s.sequenceId}
                  value={s.sequenceId}
                  cursor={"pointer"}
                  _hover={{ bgColor: "primary.50" }}
                  p={2}
                >
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>
                    <Stack gap={0}>
                      <Text fontSize="sm">
                        {formatDateString(s.capturedAt)}
                      </Text>
                      <Text fontSize="xs">Captured by {s.username}</Text>
                      {s.isPanoramic && (
                        <Text fontSize="xs">*panoramic shot*</Text>
                      )}
                    </Stack>
                  </RadioGroup.ItemText>
                </RadioGroup.Item>
              );
            })}
          </Stack>
        </RadioGroup.Root>

        <ButtonGroup
          alignSelf={"end"}
          fontSize="sm"
          size="sm"
          colorPalette={"primary"}
        >
          <Button
            variant="ghost"
            onClick={() => {
              setSequences([]);
              setAwaitingPoint(true);
            }}
          >
            Cancel
          </Button>
          <Button disabled={!selectedSequenceId} onClick={onConfirm}>
            Confirm
          </Button>
        </ButtonGroup>
      </Stack>
    );
  }

  return (
    <Stack>
      <Text fontSize="sm">Select a street by clicking a point in the map</Text>

      <ButtonGroup
        alignSelf={"end"}
        fontSize="sm"
        size="sm"
        colorPalette={"primary"}
      >
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </ButtonGroup>
    </Stack>
  );
};

export const MapillaryImportControls = observer(MapillaryImportControlsBase);
